import { Button, Modal, PreloaderCircular, TextField } from '@shared/ui/kit';
import { createPortal } from 'react-dom';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { ModalStatus } from '@features/sms-email-status';
import { useToast } from '@shared/hooks/useToast';
import { useFetchMessagesStats } from '@pages/event/api/useFetchMessagesStats';
import { FinancialWarningModal } from '@features/financial-warning-modal';
import { SendContentType } from '@pages/event/api/useSendMessages';
import { calcSmsParts20_70 } from '@shared/libs/sms-utils';
import { XmarkIcon } from '@shared/assets/icons';
import {
  HEADER_TITLES,
  INVITATION_TYPE_VALUES,
  ORGANIZER_CHANNEL_OPTIONS,
  TICKET_CHANNEL_OPTIONS,
  mapTicketChannelsToSendChannels,
} from './model/consts';
import { OrganizerChannel } from '@pages/event/api/useSendAuthQrcodeToOrganizer';
import type { OrganizerChannelOption } from './model/consts';
import type { ModalProps } from './model/types';
import { Select, type SelectValue } from '@shared/ui/select';
import { CustomDropdown } from '@shared/ui/dropdown';
import { OrganizerSending } from './ui/organizer-sending';
import { Tiptap } from '@shared/ui/tiptap';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import { StylesText } from '@entities/leadCollector';
import { mergeStylesForMail } from '@features/modal-invitation/lib/mergeStylesForMail';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';

export const ModalTickets: FC<ModalProps> = ({
  active,
  closeHandle,
  sendSMS,
  sendMessages,
  isLoading,
  isAdmin,
  usersSMS,
  eventId,
  messageAttempt,
  resetAttempts,
  isStatusVisible,
  onStatusOpen,
  onStatusClose,
  senderName,
  hasOrganizerWithoutPhone = false,
}) => {
  const [message, setMessage] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<SelectValue[]>([]);
  const [showErrorUsers, setShowErrorUsers] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [errorChannel, setErrorChannel] = useState<'sms' | 'email' | 'vk' | null>(null);
  const [isFinancialWarningOpen, setIsFinancialWarningOpen] = useState(false);
  const [showMessageError, setShowMessageError] = useState(false);
  const [showTypeError, setShowTypeError] = useState(false);
  const [organizerChannel, setOrganizerChannel] = useState<OrganizerChannel | null>(null);
  const hasEmail = selectedChannels.includes(INVITATION_TYPE_VALUES.EMAIL);
  const hasSms = selectedChannels.includes(INVITATION_TYPE_VALUES.SMS);
  const hasVk = selectedChannels.includes(INVITATION_TYPE_VALUES.VK);
  const isPlainTextMode = !hasEmail;
  const messagePlain = useMemo(
    () => (isPlainTextMode ? message.trim() : getTextFromHtml(message).trim()),
    [isPlainTextMode, message]
  );

  const { showToast } = useToast();

  const {
    messagesStats,
    error: messagesStatsError,
    refetch: refetchStats,
    fetching: isLoadingMessagesStats,
  } = useFetchMessagesStats(
    {
      eventId: eventId ?? 0,
      attempt: messageAttempt ?? null,
      contentType: SendContentType.Ticket,
    },
    !eventId || messageAttempt === null
  );

  useEffect(() => {
    if (isAdmin) return;
    if (!active || !hasSent || !eventId || !messageAttempt) {
      setIsPolling(false);
      return;
    }

    if (messagesStats?.isSent) {
      setIsPolling(false);
      return;
    }

    let cancelled = false;
    setIsPolling(true);

    const pollStats = () => {
      if (cancelled) return;

      refetchStats();

      if (!cancelled && active && hasSent) {
        setTimeout(pollStats, 5000);
        return;
      }

      setIsPolling(false);
    };

    pollStats();

    return () => {
      cancelled = true;
      setIsPolling(false);
    };
  }, [isAdmin, hasSent, eventId, messageAttempt, messagesStats?.isSent, refetchStats, active]);

  useEffect(() => {
    if (!active) {
      setHasSent(false);
      setIsPolling(false);
      setMessage('');
      setSelectedChannels([]);
      setShowErrorUsers(false);
      setErrorChannel(null);
      setShowMessageError(false);
      setShowTypeError(false);
      setOrganizerChannel(null);
    }
  }, [active]);

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!showMessageError) setShowMessageError(true);
    setMessage(event.target.value.replace(/\r?\n/g, ''));
  };

  const handleCloseFinancialWarningModal = () => {
    setIsFinancialWarningOpen(false);
  };

  const handleModalClose = () => {
    if (isStatusVisible) {
      setShowErrorUsers(false);
      setErrorChannel(null);
      onStatusClose?.();
      return;
    }
    closeHandle();
  };

  const ensureCanSend = (text: string) => {
    if (text === '') {
      showToast('Введите сообщение для отправки', 'error');
      return false;
    }
    if (!isAdmin && !selectedChannels.length) {
      showToast('Выберите тип отправки', 'error');
      return false;
    }
    return true;
  };

  const handleSendOrganizerQrcode = async () => {
    const isMessageEmpty = !messagePlain;

    setShowMessageError(isMessageEmpty);
    setShowTypeError(!organizerChannel);

    if (isMessageEmpty || !organizerChannel) return;

    setHasSent(true);

    try {
      await sendSMS(getTextFromHtml(message).trim(), organizerChannel);
    } finally {
      setHasSent(false);
    }
  };

  const sendRegularFlow = async () => {
    if (!selectedChannels.length) return;

    const channel = mapTicketChannelsToSendChannels(selectedChannels);

    await sendMessages?.({
      ticketIds: usersSMS.tickets,
      messageTemplate: hasSms || hasVk ? messagePlain : '',
      emailMessageTemplate: hasEmail ? mergeStylesForMail(message) : '',
      contentType: SendContentType.Ticket,
      channel,
    });

    onStatusOpen?.();
  };

  const handleSendMessage = async () => {
    if (!ensureCanSend(messagePlain)) {
      return;
    }

    setIsFinancialWarningOpen(false);

    try {
      setHasSent(true);
      resetAttempts?.();

      await sendRegularFlow();
    } catch (error) {
      console.error('error', error);
      showToast('Произошла ошибка при отправке приглашений', 'error');
    } finally {
      setHasSent(false);
    }
  };

  const isSendingOrganizerQrcode = isAdmin && hasSent;
  const isWaitingForTicketSend =
    !isAdmin && (hasSent || isPolling || messagesStats?.isSent === false || !!messagesStatsError);

  const getHeaderTitle = () => {
    if (isWaitingForTicketSend || isSendingOrganizerQrcode) {
      return HEADER_TITLES.SENDING;
    }
    if (showErrorUsers) {
      return HEADER_TITLES.UNDELIVERED_TICKETS;
    }
    if (isAdmin) {
      return HEADER_TITLES.SEND_QR_CODE;
    }
    if (isLoading) {
      return HEADER_TITLES.SEND_QR_TICKETS;
    }
    return HEADER_TITLES.SEND_TICKETS;
  };

  const getHeaderStyle = () => {
    if (isAdmin) {
      return styles.organizerHeader;
    }
    if (isWaitingForTicketSend || showErrorUsers || isLoading) {
      return styles.header;
    }
    return styles.headerSendTickets;
  };

  useEffect(() => {
    if (!isWaitingForTicketSend) return;

    const timer = setTimeout(() => {
      showToast('Отправка билетов началась, статус можно отслеживать в разделе мероприятия', 'success', {
        duration: 60000,
        closable: true,
      });

      closeHandle();
    }, 30000);

    return () => clearTimeout(timer);
  }, [isWaitingForTicketSend]);

  const handleChannelsChange = (values: SelectValue | SelectValue[]) => {
    if (!showTypeError) setShowTypeError(true);
    const channels = Array.isArray(values) ? values : [];
    setSelectedChannels(channels);
    const newHasEmail = channels.includes(INVITATION_TYPE_VALUES.EMAIL);
    if (!newHasEmail) {
      setMessage((prev) => getTextFromHtml(prev));
    }
  };

  const handleOrganizerChannelChange = (option: OrganizerChannelOption | null) => {
    setShowTypeError(false);
    setOrganizerChannel(option?.value ?? null);
  };

  const isVkOrganizerChannel = organizerChannel === OrganizerChannel.Vk;

  // Без телефона СМС отправить некому, поэтому убираем этот вариант из выбора
  const organizerChannelOptions = useMemo(
    () =>
      hasOrganizerWithoutPhone
        ? ORGANIZER_CHANNEL_OPTIONS.filter((option) => option.value !== OrganizerChannel.Sms)
        : ORGANIZER_CHANNEL_OPTIONS,
    [hasOrganizerWithoutPhone]
  );

  useEffect(() => {
    // Список выбранных мог измениться уже после выбора СМС
    if (hasOrganizerWithoutPhone && organizerChannel === OrganizerChannel.Sms) {
      setOrganizerChannel(null);
    }
  }, [hasOrganizerWithoutPhone, organizerChannel]);
  const organizerChannelOption = ORGANIZER_CHANNEL_OPTIONS.find(({ value }) => value === organizerChannel) ?? null;

  const messageParts = calcSmsParts20_70(messagePlain);
  const hasMessage = !!messagePlain;
  const emailCount = hasMessage ? usersSMS.tickets.length : 0;
  const smsCount = hasMessage ? messageParts * usersSMS.tickets.length : 0;

  const getMessageCount = () => {
    if (!selectedChannels.length) return '';
    const parts: string[] = [];
    if (hasVk) parts.push(`${emailCount} VK Teams`);
    if (hasEmail) parts.push(`${emailCount} email`);
    if (hasSms) parts.push(`${smsCount} смс`);
    return parts.join(' и ');
  };

  const getSendCountText = () => {
    if (isAdmin) {
      const usersCount = usersSMS?.users.length ?? 0;

      if (isVkOrganizerChannel) return `${usersCount} QR-кодов в VK Teams`;

      return `${usersCount} организаторов (${messageParts * usersCount} смс)`;
    }
    return `${usersSMS?.tickets.length} билетов (${getMessageCount()})`;
  };

  const getVkCount = () => (hasVk ? emailCount : 0);
  const getEmailCount = () => (hasEmail ? emailCount : 0);
  const getSmsCount = () => (hasSms ? smsCount : 0);

  if (!active) return null;

  const getTextarea = (showError: boolean) => {
    if (isPlainTextMode) {
      const counter = (
        <>
          {message.length}
          {!isVkOrganizerChannel && ` (${messageParts} смс)`}
        </>
      );

      if (isAdmin) {
        return (
          <div>
            <div {...stylex.props(styles.organizerTextHeader)}>
              <span>Текст *</span>
              <span>{counter}</span>
            </div>

            <TextField
              labelMsg=""
              changeFunction={handleMessageChange}
              placeholder="Введите текст"
              value={message}
              settings={{
                width: '100%',
                cursor: 'text',
                resize: 'vertical',
                minHeight: '102px',
              }}
              status={showError ? 'error' : 'default'}
              textareaMessage={showError ? 'Обязательное поле' : undefined}
            />
          </div>
        );
      }

      return (
        <>
          <p {...stylex.props(styles.textCount)}>{counter}</p>

          <TextField
            labelMsg="Текст билета *"
            changeFunction={handleMessageChange}
            placeholder="Введите текст"
            value={message}
            settings={{
              width: '100%',
              cursor: 'text',
              resize: 'vertical',
              minHeight: '78px',
            }}
            status={showError ? 'error' : 'default'}
            textareaMessage={showError ? 'Обязательное поле' : undefined}
          />
        </>
      );
    }

    return (
      <>
        <div {...stylex.props(styles.message)}>
          <Tiptap
            {...stylex.props(styles.tiptap)}
            placeholder="Текст приглашения"
            onChange={(html) => setMessage(html)}
            properties={{ link: true, styles: [StylesText.BOLD, StylesText.ITALIC, StylesText.UNDERLINE] }}
            withToolbar
            hiddenStyles={[StylesText.LEFT, StylesText.CENTER, StylesText.RIGHT, StylesText.JUSTIFY]}
            content={message}
          />
        </div>

        {(hasSms || hasVk) && hasEmail && (
          <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
            Форматирование будет применено только к email
          </Alert>
        )}
      </>
    );
  };

  const getContent = () => {
    if (isSendingOrganizerQrcode) {
      return <OrganizerSending onClose={closeHandle} />;
    }

    if (isWaitingForTicketSend) {
      return (
        <>
          <div {...stylex.props(styles.loader)}>
            <PreloaderCircular variant="secondary" size="large" />

            <p {...stylex.props(styles.text)}>
              Мы отправляем билеты.
              <br /> Процесс может занять некоторое время
            </p>
          </div>
          <div {...stylex.props(styles.btn)}>
            <Button
              size="small"
              variant="secondary"
              settings={{ padding: '8px 20px', fontSize: '14px' }}
              onClick={closeHandle}
            >
              Закрыть
            </Button>
          </div>
        </>
      );
    }

    if (isStatusVisible) {
      return (
        <ModalStatus
          messagesStats={messagesStats}
          closeHandle={onStatusClose}
          showErrorUsers={showErrorUsers}
          setShowErrorUsers={setShowErrorUsers}
          errorChannel={errorChannel}
          setErrorChannel={setErrorChannel}
          isLoading={isLoadingMessagesStats}
        />
      );
    }
    const isEmpty = !hasMessage;
    const showError = showMessageError && isEmpty;
    const isTypeEmpty = !selectedChannels.length;
    const showTypeErrorMsg = showTypeError && isTypeEmpty;

    return (
      <>
        <div {...stylex.props(isAdmin ? styles.organizerInputWrapper : styles.inputWrapper)}>
          {isAdmin ? (
            <>
              <CustomDropdown
                labelMessage="Тип отправки *"
                placeholder="Выбрать тип"
                options={organizerChannelOptions}
                itemText="name"
                itemValue="value"
                value={organizerChannelOption}
                changeFunc={handleOrganizerChannelChange}
                errorMessage={showTypeError && !organizerChannel ? 'Обязательное поле' : undefined}
                errorFloating
                isRadio={false}
                fullWidth
              />

              {hasOrganizerWithoutPhone && (
                <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
                  У части выбранных организаторов не указан телефон, поэтому отправка по СМС недоступна
                </Alert>
              )}

              {isVkOrganizerChannel && (
                <Alert variant={ALERT_VARIANTS.INFO} maxWidth="434px">
                  Все выбранные QR-коды будут отправлены в VK Teams{senderName ? `: ${senderName}` : ''}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Select
                label="Тип отправки билета *"
                placeholder="Выбрать тип"
                options={TICKET_CHANNEL_OPTIONS}
                selectedValue={selectedChannels}
                onChange={handleChannelsChange}
                isWithChips
              />
              {showTypeErrorMsg && <p {...stylex.props(styles.errorText)}>Обязательное поле</p>}
            </>
          )}

          {getTextarea(showError)}
          {!isAdmin && (
            <div {...stylex.props(styles.previewContainer)}>
              <div {...stylex.props(styles.labelPreview)}>Предварительный просмотр билета</div>
              <div {...stylex.props(styles.areaPreview)}>
                {isPlainTextMode ? (
                  <div {...stylex.props(styles.messagePreview)}>{message}</div>
                ) : (
                  <div {...stylex.props(styles.tiptap, styles.previewText)}>
                    <div dangerouslySetInnerHTML={{ __html: message }} />
                  </div>
                )}
                <div {...stylex.props(styles.linkPreview)}>{globalThis.window.location.href}</div>
              </div>
            </div>
          )}
        </div>
        <div {...stylex.props(styles.actions)}>
          <div {...stylex.props(styles.infoQuantity)}>
            <div {...stylex.props(styles.infoText)}>Будет отправлено:</div>
            <div {...stylex.props(styles.infoCount)}>{getSendCountText()}</div>
          </div>

          <Button
            onClick={closeHandle}
            variant="secondary"
            settings={{ padding: '8px 20px', fontSize: '14px' }}
            size="small"
          >
            Отмена
          </Button>

          {isAdmin && (
            <Button
              onClick={handleSendOrganizerQrcode}
              variant="accent"
              settings={{ padding: '8px 20px', fontSize: '14px' }}
              size="small"
            >
              Отправить
            </Button>
          )}

          {!isAdmin && !isLoading && (
            <Button
              onClick={() => setIsFinancialWarningOpen(true)}
              isDisabled={isEmpty || !selectedChannels.length}
              variant="accent"
              settings={{ padding: '8px 20px', fontSize: '14px' }}
              size="small"
            >
              Отправить
            </Button>
          )}
        </div>
      </>
    );
  };
  if (!active) return null;
  return (
    <>
      {createPortal(
        <Modal
          active={active}
          settingsContainer={{ height: 'auto', minWidth: isAdmin ? '490px' : '480px', padding: '0px' }}
          settingsHeader={{ padding: '0px' }}
          settingsFooter={{ padding: '0px' }}
        >
          <div {...stylex.props(styles.container)}>
            <div {...stylex.props(styles.headerContainer, isAdmin && styles.organizerHeaderContainer)}>
              <p {...stylex.props(getHeaderStyle())}>{getHeaderTitle()}</p>
              <XmarkIcon
                onClick={handleModalClose}
                size="24"
                {...stylex.props(styles.xMarkIcon, isAdmin && styles.organizerXMarkIcon)}
              />
            </div>

            {getContent()}
          </div>
        </Modal>,
        document.getElementById('modal') as Element
      )}
      <FinancialWarningModal
        open={isFinancialWarningOpen}
        onClose={handleCloseFinancialWarningModal}
        onForceSend={handleSendMessage}
        ticketsCount={usersSMS?.tickets.length}
        vkCount={getVkCount()}
        emailCount={getEmailCount()}
        smsCount={getSmsCount()}
      />
    </>
  );
};
